/**
 * Triggers a highlight pulse animation on a DOM element
 * @param element - The DOM element to highlight
 * @param options - Configuration options
 * @param options.color - CSS color value (default: 'var(--eidTrustGreen-25)')
 * @param options.duration - Animation duration in milliseconds (default: 1000)
 * @param options.inset - Background inset from element edges (default: '0')
 * @param options.radius - Border radius of the highlight background (default: 'inherit')
 * @returns A cleanup function to remove the animation class early if needed
 */
export function highlightElement(
    element: HTMLElement,
    options: {
        color?: string;
        duration?: number;
        inset?: string;
        radius?: string;
    } = {},
): () => void {
    const {
        color = 'var(--eidTrustGreen-25)',
        duration = 1000,
        inset = '0',
        radius = 'inherit',
    } = options;

    // Set CSS custom properties
    element.style.setProperty('--highlight-color', color);
    element.style.setProperty('--highlight-duration', `${duration}ms`);
    element.style.setProperty('--highlight-background-inset', inset);
    element.style.setProperty('--highlight-background-radius', radius);

    // Add animation class (animation is infinite, but we control duration by removing the class)
    element.classList.add('eid-highlight-pulse');

    // Auto-remove class after animation completes
    const timeoutId = setTimeout(() => {
        element.classList.remove('eid-highlight-pulse');
    }, duration);

    // Return cleanup function
    return () => {
        clearTimeout(timeoutId);
        element.classList.remove('eid-highlight-pulse');
    };
}

